import { useEffect, useState } from "react";
import { Controller, useFormState, useWatch, type Control } from "react-hook-form";
import { Link } from "react-router-dom";
import type { RankingTable, RankingStatus } from "@/api/researchCms.service";
import type { OrgFormValues } from "../../schemas/orgViewSchemas";
import { CmsEmptyState } from "@/components/ResearchCms";
import { PublishableIcon, InternalIcon } from "../SectionVisibilityIcon";
import { ROUTES } from "@/routes";
import shared from "../cmsShared.module.css";
import styles from "./RankingsTab.module.css";

type RankingsTabProps = {
  tables: RankingTable[];
  scopedTableId?: string;
  entityType: "firm" | "lawyer";
  canEdit: boolean;
  canApprove?: boolean;
  control: Control<OrgFormValues>;
  previousTiers?: Record<string, number | null>;
  editionId?: string;
  bpaId?: string;
};

const TIER_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
const RANKING_STATUSES: RankingStatus[] = ["draft", "pending", "published", "archived"];

const STATUS_LABELS: Record<RankingStatus, string> = {
  draft: "Draft",
  pending: "Pending",
  published: "Published",
  archived: "Archived",
};

export const SUBTYPE_LABELS: Record<string, string> = {
  firm_recommended: "Firm Recommended",
  firm_to_watch: "Firms to Watch",
  commentary: "Commentary",
  lawyer_leading_individuals: "Leading Individuals",
  lawyer_hall_of_fame: "Hall of Fame",
  lawyer_next_generation: "Next Generation",
  lawyer_rising_stars: "Rising Stars",
  lawyer_recommended: "Lawyer Recommended",
  lawyer_leading_juniors: "Leading Juniors",
  lawyer_leading_silks: "Leading Silks",
  lawyer_new_silks: "New Silks",
  lawyer_leading_lawyers: "Leading Lawyers",
};

export function RankingsTab({
  tables,
  scopedTableId,
  entityType,
  canEdit,
  canApprove = false,
  control,
  previousTiers,
  editionId,
  bpaId,
}: RankingsTabProps) {
  const relevantTables = tables.filter(t =>
    entityType === "firm" ? t.isLawyerTable === 0 : t.isLawyerTable === 1,
  );

  if (relevantTables.length === 0) {
    return <CmsEmptyState message="No ranking tables for this practice area." />;
  }

  const sorted = scopedTableId
    ? [
        ...relevantTables.filter(t => t.id === scopedTableId),
        ...relevantTables.filter(t => t.id !== scopedTableId),
      ]
    : relevantTables;

  return (
    <div className={styles.list}>
      {sorted.map(table => (
        <RankingSection
          key={table.id}
          table={table}
          isScoped={table.id === scopedTableId}
          defaultOpen={table.id === scopedTableId || !scopedTableId}
          canEdit={canEdit}
          canApprove={canApprove}
          control={control}
          previousTier={previousTiers?.[table.id] ?? null}
          editionId={editionId}
          bpaId={bpaId}
        />
      ))}
    </div>
  );
}

const RESEARCHER_STATUSES: RankingStatus[] = ["draft", "pending"];

type RankingSectionProps = {
  table: RankingTable;
  isScoped: boolean;
  defaultOpen: boolean;
  canEdit: boolean;
  canApprove: boolean;
  control: Control<OrgFormValues>;
  previousTier: number | null;
  editionId?: string;
  bpaId?: string;
};

function RankingSection({
  table,
  isScoped,
  defaultOpen,
  canEdit,
  canApprove,
  control,
  previousTier,
  editionId,
  bpaId,
}: RankingSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isTiered = table.isTiered === 1;
  const subtypeLabel = SUBTYPE_LABELS[table.subtype] ?? table.subtype;

  const watchedTier = useWatch({ control, name: `rankings.${table.id}.tier` });
  const watchedRanked = useWatch({ control, name: `rankings.${table.id}.ranked` });
  const watchedStatus = useWatch({ control, name: `rankings.${table.id}.status` });
  const { errors } = useFormState({ control, name: `rankings.${table.id}.tier` });
  const rankingError = errors.rankings?.[table.id]?.tier?.message;

  useEffect(() => {
    if (rankingError) setOpen(true);
  }, [rankingError]);

  const viewTableUrl =
    editionId && bpaId ? ROUTES.research.cmsRankingTable(editionId, bpaId, table.id) : null;

  return (
    <div className={`${styles.section} ${isScoped ? styles.sectionScoped : ""}`}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
      >
        <span
          className={styles.chevron}
          style={{ transform: open ? undefined : "rotate(-90deg)" }}
          aria-hidden="true"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2 4l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {viewTableUrl ? (
          <Link
            to={viewTableUrl}
            className={styles.sectionNameLink}
            onClick={e => e.stopPropagation()}
          >
            {table.name}
          </Link>
        ) : (
          <span className={styles.sectionName}>{table.name}</span>
        )}
        <span className={styles.subtypeBadge}>{subtypeLabel}</span>
        <span className={styles.sectionSpacer} />
        <span className={styles.headerMeta}>
          {isTiered ? (
            <>
              Tier <strong>{watchedTier ?? "—"}</strong>
              {previousTier !== null && (
                <span className={styles.headerPrev}>Previous: {previousTier}</span>
              )}
            </>
          ) : (
            <span className={watchedRanked ? styles.tickBadge : ""}>
              {watchedRanked ? "✓" : "—"}
            </span>
          )}
        </span>
        <span className={styles.headerStatus}>
          {STATUS_LABELS[watchedStatus as RankingStatus] ?? "Draft"}
        </span>
      </button>

      {open && (
        <div className={styles.sectionBody}>
          <div className={styles.fieldsRow}>
            {isTiered ? (
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  Tier <PublishableIcon tooltip="This ranking tier is shown on legal500.com" />
                </label>
                <Controller
                  name={`rankings.${table.id}.tier`}
                  control={control}
                  render={({ field }) => (
                    <select
                      className={styles.select}
                      value={field.value ?? ""}
                      onChange={e => {
                        const val = e.target.value;
                        field.onChange(val === "" ? null : Number(val));
                      }}
                      disabled={!canEdit}
                    >
                      <option value="">&#8212;</option>
                      {TIER_LEVELS.map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  )}
                />
                <span className={styles.prevTier}>Previous tier: {previousTier ?? "—"}</span>
              </div>
            ) : (
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  Ranked <PublishableIcon tooltip="This ranking is shown on legal500.com" />
                </label>
                <Controller
                  name={`rankings.${table.id}.ranked`}
                  control={control}
                  render={({ field }) => (
                    <button
                      type="button"
                      className={`${styles.toggleBadge} ${field.value ? styles.toggleBadgeOn : ""}`}
                      onClick={() => {
                        if (canEdit) field.onChange(!field.value);
                      }}
                      disabled={!canEdit}
                      aria-pressed={field.value ?? false}
                    >
                      {field.value ? "✓" : "—"}
                    </button>
                  )}
                />
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Display Order{" "}
                <PublishableIcon tooltip="Controls the order this entry appears in the ranking table" />
              </label>
              <Controller
                name={`rankings.${table.id}.displayOrder`}
                control={control}
                render={({ field }) => (
                  <input
                    type="number"
                    className={styles.select}
                    min={0}
                    value={field.value ?? 0}
                    onChange={e => field.onChange(Number(e.target.value))}
                    disabled={!canEdit}
                  />
                )}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Status <InternalIcon tooltip="Internal only — not shown on legal500.com" />
              </label>
              <Controller
                name={`rankings.${table.id}.status`}
                control={control}
                render={({ field }) => (
                  <select
                    className={styles.select}
                    value={field.value ?? "draft"}
                    onChange={e => field.onChange(e.target.value)}
                    disabled={!canEdit}
                  >
                    {(canApprove ? RANKING_STATUSES : RESEARCHER_STATUSES).map(s => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          {rankingError && (
            <p className={shared.fieldError} role="alert">
              {rankingError}
            </p>
          )}

          <div className={styles.commentField}>
            <label className={styles.fieldLabel}>
              Ranking comment <InternalIcon tooltip="Internal only — not shown on legal500.com" />
            </label>
            <Controller
              name={`rankings.${table.id}.comment`}
              control={control}
              render={({ field }) => (
                <textarea
                  className={shared.textarea}
                  rows={3}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  disabled={!canEdit}
                  placeholder="Add a ranking comment..."
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
